import { CSSResultGroup, html, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './stepper-input.style.js'
import { ValueFormElement } from 'elements/_shared/formElement.js'
import { formFieldValidationTypes } from 'elements/form-field/form-field.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'


@customElement('cse-stepper-input')
export class CSEStepperInput extends ValueFormElement {
  static override styles: CSSResultGroup = [hostStyle, fontStyles, style]

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties)
    this.checkRequired()
  }

  @property({ type: Number })
  min?: number

  @property({ type: Number })
  max?: number

  @property({ type: Number })
  step?: number

  @property({ type: String, reflect: true })
  value = ''

  @property({ type: Boolean, reflect: true })
  required!: boolean

  @property({ type: String, attribute: 'required-text' })
  requiredText?: string

  @property({ type: Boolean })
  disabled: boolean = false

  @property({ type: String })
  valid: formFieldValidationTypes[number] = 'none'

  @property({ type: String, reflect: true })
  name = 'cse-stepper-input'

  @state()
  decreaseDisabled: boolean = false

  @state()
  increaseDisabled: boolean = false

  @query('input')
  private input!: HTMLInputElement

  private onInput = () => {
    this.checkRequired()
  }

  private onIncreaseClicked = () => {
    if(this.disabled || this.increaseDisabled) return

    this.increase()
  }

  private onDecreaseClicked = () => {
    if(this.disabled || this.decreaseDisabled) return

    this.decrease()
  }

  private setValue(value: string) {
    if (this.value === value) { return }

    this.value = value
    this.emit(value)
  }

  private decrease() {
    const minimum = parseFloat(this.input.min)
    const step = parseFloat(this.input.step) || 1
    const stepSubceedsMinimum = ((parseFloat(this.input.value) || 0) - step) < minimum

    if (stepSubceedsMinimum) {
      return
    }

    this.input.stepDown()
    this.checkRequired()
    this.setValue(this.input.value)
  }

  private increase() {
    const maximum = parseFloat(this.input.max)
    const step = parseFloat(this.input.step) || 1
    const stepExceedsMaximum = ((parseFloat(this.input.value) || 0) - step) > maximum

    if (stepExceedsMaximum) {
      return
    }

    this.input.stepUp()
    this.checkRequired()
    this.setValue(this.input.value)
  }

  private checkInput() {
    const value = parseFloat(this.input.value)
    const min = parseFloat(this.input.min) || 0
    const max = parseFloat(this.input.max)
    const step = parseFloat(this.input.step) || 1

    const steppedValue = parseFloat((Math.floor(parseFloat((value / step).toFixed(10))) * step).toFixed(10))

    if (min && steppedValue < min) {
      this.input.value = min.toString()
    } else if (max && steppedValue > max) {
      this.input.value = max.toString()
    } else {
      this.input.value = steppedValue.toString()
    }

    this.decreaseDisabled = value <= min
    this.increaseDisabled = value >= max

    this.checkRequired()
    this.setValue(this.input.value)
  }

  private checkRequired() {
    if (this.required && this.input.value.trim() === '') {
      this.internals.setValidity({
        valueMissing: true
      }, this.requiredText || 'This field is required', this.input)
    } else {
      this.internals.setValidity({})
    }
  }

  protected override updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('value')) {
      this.input.value = this.value
      this.checkInput()
    }
    super.updated(changedProperties)
  }

  override render() {
    const variableStep = this.step ?? 1

    return html`
      <div class="${classMap({
        'cse-stepper-input': true,
        [`valid-${this.valid}`]: true,
      })}">
        <button
          class="button font-body decrease-button"
          variant="secondary"
          ?disabled=${this.disabled || this.decreaseDisabled}
          @click=${this.onDecreaseClicked}
          aria-label="${t('decrease_value', { ns: 'cse-stepper-input', default: 'Decrease value by {{variable_step}}', variable_step: variableStep })}"
        >
          <slot name="decrease-button">
            <cse-icon icon-role="cse-stepper-input.decrease"></cse-icon>
          </slot>
        </button>

        <input
          type="number"
          min=${ifDefined(this.min)}
          max=${ifDefined(this.max)}
          step=${ifDefined(this.step)}
          value=${this.value}
          name=${this.name}
          ?disabled=${this.disabled}
          ?required=${this.required}
          @change=${this.checkInput}
          @input=${this.onInput}
        />

        <button
          class="button font-body increase-button"
          variant="secondary"
          ?disabled=${this.disabled || this.increaseDisabled}
          @click=${this.onIncreaseClicked}
          aria-label="${t('increase_value', { ns: 'cse-stepper-input', default: 'Increase value by {{variable_step}}', variable_step: variableStep  })}"
        >
          <slot name="increase-button">
            <cse-icon icon-role="cse-stepper-input.increase"></cse-icon>
          </slot>
        </button>
      </div>
    `
  }
}

initializeIconsByRole({
  'cse-stepper-input.decrease': { name: 'minus', style: 'light' },
  'cse-stepper-input.increase': { name: 'plus', style: 'light' },
})


declare global {
  interface HTMLElementTagNameMap {
    'cse-stepper-input': CSEStepperInput
  }
}
